(() => {
  const SPACER_ID = 'infinite-scroll-spacer';
  const CARD_SEL = `:scope > .grid__item:not(#${SPACER_ID})`;
  const MAX_RETRIES = 3;
  const MAX_EMPTY_PAGES = 5;

  let loading = false;
  let hasMore = true;
  let revealObs;
  let retries = 0;
  let emptyPages = 0;
  let scrollBound = false;

  const isMobile = () => matchMedia('(max-width: 749px)').matches;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const gridEl = () => document.querySelector('#product-grid');
  const metaEl = () => document.getElementById('infinite-scroll-meta');
  const getSpacer = (grid) => grid.querySelector(`#${SPACER_ID}`);

  function nextUrlFrom() {
    const grid = gridEl();
    const meta = metaEl();
    return grid?.dataset.nextUrl || meta?.dataset.nextUrl || '';
  }

  function setNextUrl(url) {
    const grid = gridEl();
    const meta = metaEl();

    if (grid) {
      if (url) grid.dataset.nextUrl = url;
      else delete grid.dataset.nextUrl;
    }

    if (meta) {
      if (url) meta.dataset.nextUrl = url;
      else meta.remove();
    }
  }

  function cardHeight(grid) {
    const card = grid.querySelector(CARD_SEL);
    if (!card) return 0;
    const styles = getComputedStyle(grid);
    const gap = parseFloat(styles.rowGap) || parseFloat(styles.gap) || 0;
    return Math.ceil(card.getBoundingClientRect().height + gap);
  }

  function productKey(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return card.outerHTML.slice(0, 120);
    try {
      return new URL(link.getAttribute('href'), location.origin).pathname;
    } catch {
      return link.getAttribute('href');
    }
  }

  function existingProductKeys(grid) {
    return new Set([...grid.querySelectorAll(CARD_SEL)].map(productKey));
  }

  function injectStyles() {
    if (document.getElementById('infinite-scroll-styles')) return;

    const style = document.createElement('style');
    style.id = 'infinite-scroll-styles';
    style.textContent = `
      #${SPACER_ID}{grid-column:1/-1;width:100%;height:1px;pointer-events:none;opacity:0}
      #product-grid>.grid__item.infinite-scroll-item{opacity:0;transform:translateY(16px);transition:opacity .55s ease,transform .55s ease}
      #product-grid>.grid__item.infinite-scroll-item.is-visible{opacity:1;transform:none}
      @media(prefers-reduced-motion:reduce){#product-grid>.grid__item.infinite-scroll-item{opacity:1;transform:none;transition:none}}
    `;
    document.head.appendChild(style);
  }

  function ensureSpacer(grid) {
    let spacer = getSpacer(grid);
    if (!spacer) {
      spacer = document.createElement('li');
      spacer.id = SPACER_ID;
      spacer.setAttribute('aria-hidden', 'true');
      grid.appendChild(spacer);
    }

    const h = cardHeight(grid);
    if (h) spacer.style.height = `${h}px`;
    return spacer;
  }

  function removeSpacer(grid) {
    getSpacer(grid)?.remove();
  }

  function revealObserver() {
    if (revealObs) return revealObs;

    revealObs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          requestAnimationFrame(() => entry.target.classList.add('is-visible'));
          revealObs.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
    );

    return revealObs;
  }

  async function appendCards(grid, items) {
    const delay = isMobile() ? 40 : 50;
    const obs = revealObserver();
    const quiet = reduceMotion();
    const spacer = getSpacer(grid);

    for (let i = 0; i < items.length; i++) {
      const card = items[i].cloneNode(true);
      card.classList.remove('scroll-trigger', 'animate--slide-in');
      card.classList.add('infinite-scroll-item');
      card.style.removeProperty('--animation-order');
      card.removeAttribute('data-cascade');
      spacer ? grid.insertBefore(card, spacer) : grid.appendChild(card);
      quiet ? card.classList.add('is-visible') : obs.observe(card);
      if (i < items.length - 1) await sleep(delay);
    }

    document.dispatchEvent(new CustomEvent('collection:grid:appended'));
  }

  function finish() {
    hasMore = false;
    loading = false;
    setNextUrl('');
    removeSpacer(gridEl());
    if (scrollBound) {
      removeEventListener('scroll', onScroll);
      scrollBound = false;
    }
  }

  function loadOffset() {
    return isMobile() ? 320 : 480;
  }

  function distToBottom(grid) {
    const spacer = getSpacer(grid);
    const target = spacer || grid;
    return target.getBoundingClientRect().bottom - innerHeight;
  }

  async function loadMore() {
    const grid = gridEl();
    const url = nextUrlFrom();

    if (loading || !hasMore || !grid || !url) {
      if (grid && !url) finish();
      return;
    }

    loading = true;

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector('#product-grid');
      const fetched = [...(nextGrid?.querySelectorAll(CARD_SEL) || [])];
      const known = existingProductKeys(grid);
      const items = fetched.filter((item) => !known.has(productKey(item)));
      const upcoming =
        nextGrid?.dataset.nextUrl || html.getElementById('infinite-scroll-meta')?.dataset.nextUrl || '';

      setNextUrl(upcoming);
      retries = 0;

      if (items.length) {
        emptyPages = 0;
        await appendCards(grid, items);
      } else {
        emptyPages += 1;
      }

      if (!upcoming || emptyPages >= MAX_EMPTY_PAGES) {
        finish();
        return;
      }

      if (!items.length) {
        loading = false;
        await loadMore();
        return;
      }

      ensureSpacer(grid);
    } catch (e) {
      console.error('Infinite scroll error:', e);
      retries += 1;
      if (retries >= MAX_RETRIES) finish();
    } finally {
      loading = false;
    }
  }

  function onScroll() {
    const grid = gridEl();
    if (!grid || !hasMore || loading) return;
    if (distToBottom(grid) <= loadOffset()) loadMore();
  }

  function init() {
    const grid = gridEl();
    if (!grid) return;

    if (scrollBound) {
      removeEventListener('scroll', onScroll);
      scrollBound = false;
    }

    loading = false;
    hasMore = true;
    retries = 0;
    emptyPages = 0;

    injectStyles();

    if (!nextUrlFrom()) {
      hasMore = false;
      return;
    }

    ensureSpacer(grid);
    addEventListener('scroll', onScroll, { passive: true });
    scrollBound = true;
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('collection:grid:updated', init);
})();
